import React, { lazy, Suspense, useEffect, useState } from 'react';
import styled from 'styled-components';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import _get from 'lodash/get';
import Checkbox from '../../../../../styledComponents/styles/Checkbox.styled';
import StyledIcon from '../../../../../styledComponents/styles/Icon.styled';
import TextInput from '../../../../../styledComponents/styles/TextInput.styled';
import Toggle from '../../../../../styledComponents/styles/Toggle.styled';
import { AccentButtonMediumNunito } from '../../../../../styledComponents/styles/Buttons.styled';
import { AccentButtonSpinner } from '../../../../../styledComponents/styles/spinner.styled';
import { updateForm, updateQuestion } from '../../../../../actions/formsActions';

const EditTextArea = lazy(() => import('../../../../../styledComponents/styles/EditTextArea.styled'));

const FormContainer = styled.div`
  font-family: 'Nunito', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 120%;
  letter-spacing: 0.015em;
  color: #373737;
`;

const InputContainer = styled.div`
  flex: 1;
  margin: 20px 0;
  text-align: left;
`;

const SubmitContainer = styled.main`
  display: flex;
  justify-content: flex-end;
  margin-top: 35px;
`;
const ToggleContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: inherit;
`;

const ToggleLabel = styled.span`
  align-self: center;
  flex: 1;
  justify-content: center;
  padding-left: 10px;
  padding-bottom: 7px;
`;

const InfoMsg = styled.div`
  background-color: #deecff;
  border-left: 20px solid #5180b1;
  border-radius: 5px;
  color: #4d4d4d;
  line-height: 17px;
  margin-top: 30px;
  margin-bottom: 30px;
  padding: 20px;
  width: 100%;
`;

export default function QuestionDatePickerAddEdit({ question, edit, onClose }) {
  const MAX_LENGTH = 90;
  const qIdentifier = 'date';
  const dispatch = useDispatch();
  const fetchForm = useSelector(state => state.forms.fetchForm);
  const questionTypesInfo = useSelector(state => state.forms.questionTypes);
  const updateFormSubmit = useSelector(state => state.forms.updateForm);
  const updateQuestionSubmit = useSelector(state => state.forms.updateQuestion);
  const [formId, setFormId] = useState(null);
  const customAliases = useSelector(state => state.profile.customAliases);
  const isTravelerInfo = ['travelerinfofield', 'customfield'].includes(question?.sourceable_type?.toLowerCase());
  const [disableSubmit, setDisableSubmit] = useState(false);

  const schema = yup.object().shape({
    date_label: yup
      .string()
      .required('Question must have label')
      .max(MAX_LENGTH, `Label can not be more than ${MAX_LENGTH} characters`),
    date_instructions: yup.string(),
    date_admin_only: yup.boolean(),
    date_required: yup.boolean(),
    date_future_only: yup.boolean(),
  });

  const { control, handleSubmit, formState, setValue } = useForm({
    shouldUnregister: false,
    resolver: yupResolver(schema),
    defaultValues: {
      date_label: edit ? question.label : '',
      date_instructions: edit ? question.instructions || '' : '',
      date_admin_only: false,
      date_required: edit ? question.required : true,
      date_future_only: edit ? question.future_only : false,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!fetchForm?.loading && fetchForm?.data) {
      setDisableSubmit(false);
      setFormId(fetchForm.data.id);
    }
  }, [fetchForm]);

  useEffect(() => {
    if (
      (!updateFormSubmit?.loading && updateFormSubmit?.data) ||
      (!updateQuestionSubmit?.loading && updateQuestionSubmit?.data)
    ) {
      setDisableSubmit(false);
      onClose();
    }
  }, [updateFormSubmit, updateQuestionSubmit]);

  const onSubmitHandler = data => {
    if (questionTypesInfo && questionTypesInfo.data) {
      const questionType = questionTypesInfo.data.reduce((prev, curr) => {
        return prev || curr.attributes.question_types.find(question => question.identifier === qIdentifier);
      }, undefined);

      const values = {
        admin_only: false,
        instructions: data.date_instructions,
        label: data.date_label,
        required: data.date_required,
        future_only: data.date_future_only,
      };

      if (formId && data && (questionType?.id || question?.id)) {
        //update or save new
        const actionToCall = edit ? updateQuestion : updateForm;
        const idToUpdate = edit ? question.id : questionType.id;
        setDisableSubmit(true);
        dispatch(actionToCall(formId, questionType.endpoint, idToUpdate, values));
      }
    }
  };

  return (
    <FormContainer>
      <form id={'datePickerAdd'} onSubmit={handleSubmit(onSubmitHandler)}>
        {isTravelerInfo && (
          <TextInput
            aria-label={'Traveler Info Field'}
            errorMsg=""
            id={'readonly_label'}
            label={'Traveler Info Field'}
            openSans
            placeholder={question?.sourceable_attributes?.display_name}
            readOnly
            disabled={true}
            icon={'Sync'}
          />
        )}
        <Controller
          control={control}
          name={'date_label'}
          render={({ field: { ref, ...rest } }) => (
            <InputContainer>
              <TextInput
                aria-label={'Date Picker Label'}
                errorMsg={_get(formState.errors.date_label, 'message') || ''}
                id={'date_label'}
                label={isTravelerInfo ? 'Label' : 'Date Picker Label'}
                openSans
                placeholder={'Enter Label'}
                required
                {...rest}
              />
            </InputContainer>
          )}
        />
        {!isTravelerInfo && (
          <>
            <Controller
              control={control}
              name={'date_instructions'}
              render={({ field: { ref, ...rest } }) => (
                <InputContainer>
                  <Suspense fallback={<div />}>
                    <EditTextArea
                      aria-label="Instructions"
                      errorMsg={_get(formState.errors.date_instructions, 'message') || ''}
                      placeholder={`Enter Instructions`}
                      label={`Instructions`}
                      id={'date_instructions'}
                      {...rest}
                    />
                  </Suspense>
                </InputContainer>
              )}
            />

            <Controller
              control={control}
              name={'date_future_only'}
              render={({ field: { ref, ...rest } }) => (
                <InputContainer>
                  <Checkbox
                    // defaultChecked={date_future_only}
                    id={'date_future_only'}
                    label={''}
                    errorMsg={_get(formState.errors.date_future_only, 'message') || ''}
                    {...rest}
                  >
                    {' Allow only future dates'}
                  </Checkbox>
                </InputContainer>
              )}
            />
          </>
        )}
        {isTravelerInfo && (
          <InfoMsg>
            {`${customAliases.alias_traveler} info fields will be auto-populated 
            from ${customAliases.alias_traveler.toLowerCase()}’s profile 
            if the ${customAliases.alias_traveler.toLowerCase()} has filled 
            out their profile.`}
          </InfoMsg>
        )}
        <Controller
          control={control}
          name={'date_required'}
          render={({ field: { onChange, register, ref, ...rest } }) => (
            <ToggleContainer>
              <Toggle
                id={'date_required'}
                label={'Required'}
                onChange={value => {
                  setValue('date_required', value);
                }}
                ref={register}
                {...rest}
              />
              <ToggleLabel>Required Field</ToggleLabel>
            </ToggleContainer>
          )}
        />

        <SubmitContainer>
          <AccentButtonMediumNunito
            aria-label={edit ? 'Save Date Picker' : 'Add Date Picker to Form'}
            form="datePickerAdd"
            type={'submit'}
            disabled={disableSubmit}
          >
            {disableSubmit ? <AccentButtonSpinner displayText='Saving ...' /> : edit ? 'Save' : 'Add to Form'}
            {!disableSubmit && <StyledIcon type='ChevronForward' color='#fff' />}
          </AccentButtonMediumNunito>
        </SubmitContainer>
      </form>
    </FormContainer>
  );
}
